/*
 * NTT fix 09/17/26 (Rabin M Joshi)
 * The original Zest version re-wrote img.sizes / img.width / img.height from an
 * IntersectionObserver. Doing that on an image the browser has already started
 * fetching aborts the request (WebKit then paints the alt text) and can write
 * width="0" when the wrapper has no layout yet. On a reload everything is cached
 * so the bug hides itself. This version only touches images that have not
 * started loading, never writes width/height, and always clears the loading
 * state on load or error.
 */
class ResponsiveImage extends HTMLElement {
	get intersecting() {
		return this.hasAttribute('intersecting')
	}
	constructor() {
		super()
		this.img = this.querySelector('img')
		this.observerCallback = this.observerCallback.bind(this)
		this.loadImage = this.loadImage.bind(this)
		this.onLoad = this.onLoad.bind(this)
		if (!this.img) return
		this.img.addEventListener('load', this.onLoad)
		this.img.addEventListener('error', this.onLoad)
		if (this.img.complete) this.onLoad()
	}
	connectedCallback() {
		if (!this.img) {
			this.removeAttribute('data-image-loading')
			return
		}
		// Eager images and anything the browser has already picked a source for
		// are in flight or done. Leave them alone.
		if (this.img.complete || this.img.loading !== 'lazy' || this.img.currentSrc) {
			this.setAttribute('intersecting', 'true')
			return
		}
		if ('IntersectionObserver' in window) this.initIntersectionObserver()
		else this.loadImage()
	}
	disconnectedCallback() {
		this.disconnectObserver()
	}
	loadImage() {
		this.setAttribute('intersecting', 'true')
		if (!this.img || this.img.complete || this.img.currentSrc) return
		var width = Math.round(this.getBoundingClientRect().width)
		if (width > 0) this.img.sizes = width + 'px'
	}
	onLoad() {
		this.removeAttribute('data-image-loading')
		if (this.img) this.img.classList.add('f-img-loaded')
	}
	observerCallback(entries, observer) {
		if (!entries[0].isIntersecting) return
		observer.unobserve(this)
		this.loadImage()
	}
	initIntersectionObserver() {
		if (this.observer) return
		this.observer = new IntersectionObserver(this.observerCallback, { rootMargin: '200px' })
		this.observer.observe(this)
	}
	disconnectObserver() {
		if (!this.observer) return
		this.observer.disconnect()
		this.observer = null
		delete this.observer
	}
}
if (!customElements.get('responsive-image')) {
	customElements.define('responsive-image', ResponsiveImage)
}

/*
 * NTT safety net 09/23/26: some visitors saw broken image icons on the
 * homepage (the tea-type row and the "Why Buy" photo) when a request
 * failed mid-load on a first visit. The URLs and markup are correct, so a
 * failed image is retried exactly once by re-setting its own attributes.
 * Runs after page load and again at 3s and 8s, plus after the first scroll
 * (for lazy images) and on back/forward restores. Images that loaded fine
 * are never touched. setTimeout (not requestAnimationFrame) so it still
 * runs in background tabs.
 */
;(function () {
	function retryOnce(img) {
		if (!img || img.dataset.nttRetried) return
		if (!img.complete || img.naturalWidth > 0) return
		var src = img.getAttribute('src')
		var srcset = img.getAttribute('srcset')
		if (!src && !srcset) return
		img.dataset.nttRetried = '1'
		img.removeAttribute('srcset')
		img.removeAttribute('src')
		setTimeout(function () {
			if (srcset) img.setAttribute('srcset', srcset)
			if (src) img.setAttribute('src', src)
		}, 50)
	}
	function sweep() {
		var imgs = document.querySelectorAll('img')
		for (var i = 0; i < imgs.length; i++) retryOnce(imgs[i])
	}
	function schedule() {
		sweep()
		setTimeout(sweep, 3000)
		setTimeout(sweep, 8000)
	}
	document.addEventListener(
		'error',
		function (e) {
			var t = e.target
			if (t && t.tagName === 'IMG') setTimeout(function () { retryOnce(t) }, 500)
		},
		true
	)
	if (document.readyState === 'complete') schedule()
	else window.addEventListener('load', schedule)
	window.addEventListener('pageshow', function (e) {
		if (e.persisted) schedule()
	})
	document.addEventListener(
		'scroll',
		function onFirstScroll() {
			document.removeEventListener('scroll', onFirstScroll)
			setTimeout(sweep, 1500)
		},
		{ passive: true }
	)
})()
